import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Copy, MoreVertical, Plus, Image, LayoutGrid, Building2, Users } from 'lucide-react';
import { Container } from '../../components/ui/Container/Container';
import {
	DropdownMenu,
	DropdownMenuTrigger,
	DropdownMenuContent,
	DropdownMenuItem,
} from '../../components/ui';
import { canvasService } from '../../services/canvasService';
import { useOrgStore } from '../../stores/orgStore';
import { DashboardPaywallBanner } from '../../components/Paywall/DashboardPaywallBanner';
import type { CanvasData } from '../../types/canvas';

/**
 * Dashboard - Focused design
 * Primary goal: Get users into a canvas fast
 * - Big "New Canvas" CTA front and center
 * - Recent canvases as horizontal cards with node count
 * - Quick links section below
 */
export function DashboardPage() {
	const navigate = useNavigate();
	const orgId = useOrgStore((s) => s.activeOrgId);
	const [recentCanvases, setRecentCanvases] = useState<CanvasData[]>([]);
	const [isLoading, setIsLoading] = useState(true);
	const [isCreating, setIsCreating] = useState(false);
	const [duplicatingId, setDuplicatingId] = useState<string | null>(null);

	useEffect(() => {
		if (orgId) {
			canvasService.list()
				.then(({ data }) => {
					const sorted = [...data].sort((a: CanvasData, b: CanvasData) =>
						new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()
					).slice(0, 6);
					setRecentCanvases(sorted);
				})
				.catch(() => {})
				.finally(() => setIsLoading(false));
		}
	}, [orgId]);

	const handleNewCanvas = async () => {
		try {
			setIsCreating(true);
			const { data } = await canvasService.create({ name: 'New Canvas' });
			navigate(`/canvas/${data.id}`);
		} catch (err) {
			console.error('Failed to create canvas:', err);
			setIsCreating(false);
		}
	};

	const handleDuplicate = async (canvasId: string) => {
		setDuplicatingId(canvasId);
		try {
			const { data } = await canvasService.duplicate(canvasId);
			navigate(`/canvas/${data.canvas.id}`);
		} catch (err) {
			console.error('Failed to duplicate canvas:', err);
			setDuplicatingId(null);
		}
	};

	const formatDate = (dateStr: string) => {
		const date = new Date(dateStr);
		const now = new Date();
		const diffMs = now.getTime() - date.getTime();
		const diffDays = Math.floor(diffMs / 86400000);
		if (diffDays === 0) return 'Today';
		if (diffDays === 1) return 'Yesterday';
		if (diffDays < 7) return `${diffDays} days ago`;
		return date.toLocaleDateString();
	};

	return (
		<div className="min-h-[calc(100vh-48px)] w-full relative overflow-hidden">
			{/* Soft gradient background inspired by homepage hero */}
			<div className="absolute inset-0 pointer-events-none" style={{
				background: 'linear-gradient(135deg, rgba(218,236,246,0.3) 0%, rgba(238,227,243,0.2) 50%, rgba(250,252,232,0.15) 100%)',
			}} />

			{/* Subtle dot grid */}
			<div className="absolute inset-0 pointer-events-none opacity-40" style={{
				backgroundImage: 'radial-gradient(circle, rgba(62,69,87,0.08) 1px, transparent 1px)',
				backgroundSize: '24px 24px',
			}} />

			<Container size="lg" className="relative z-10 py-12">
				{/* Hero Section - New Canvas CTA */}
				<div className="text-center mb-12">
					<h1 className="text-3xl md:text-4xl font-bold text-navy-800 mb-3">
						What will you create today?
					</h1>
					<p className="text-lg text-navy-500 mb-8 max-w-md mx-auto">
						Build visual AI workflows to generate stunning images and videos
					</p>

					<button
						onClick={handleNewCanvas}
						disabled={isCreating}
						className="inline-flex items-center gap-3 px-8 py-4 rounded-xl text-lg font-semibold transition-all hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed"
						style={{
							background: '#c8d45a',
							color: '#1d2939',
							boxShadow: '0 4px 20px rgba(200,212,90,0.35), 0 2px 8px rgba(0,0,0,0.08)',
						}}
					>
						<Plus className="w-6 h-6" />
						{isCreating ? 'Creating...' : 'New Canvas'}
					</button>
				</div>

				{/* Paywall Banner — shown when credits exhausted */}
				<DashboardPaywallBanner />

				{/* Recent Canvases - Horizontal Cards */}
				<div className="mb-10">
					<div className="flex items-center justify-between mb-5">
						<h2 className="text-lg font-semibold text-navy-700">Recent Canvases</h2>
						<button
							onClick={() => navigate('/organization/canvases')}
							className="text-sm text-navy-400 hover:text-primary-600 transition-colors"
						>
							View all &rarr;
						</button>
					</div>

					{isLoading ? (
						<div className="flex items-center justify-center py-16">
							<div className="w-8 h-8 border-3 border-primary-400 border-t-transparent rounded-full animate-spin" />
						</div>
					) : recentCanvases.length === 0 ? (
						<div className="text-center py-16 bg-white/60 backdrop-blur-sm rounded-2xl border border-white/80">
							<div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-primary-100 flex items-center justify-center">
								<Image className="w-8 h-8 text-primary-500" />
							</div>
							<p className="text-navy-500 mb-2">No canvases yet</p>
							<p className="text-sm text-navy-400">Create your first canvas to get started</p>
						</div>
					) : (
						<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
							{recentCanvases.map((canvas) => (
								<div
									key={canvas.id}
									className="group relative text-left p-5 rounded-xl bg-white/70 backdrop-blur-sm border border-white/80 hover:bg-white hover:shadow-lg hover:border-primary-200 transition-all cursor-pointer"
									onClick={() => navigate(`/canvas/${canvas.id}`)}
								>
									<div className="flex items-start gap-3">
										<div className="min-w-0 flex-1">
											<h3 className="font-medium text-navy-800 truncate group-hover:text-primary-700 transition-colors">
												{canvas.name}
											</h3>
											<div className="flex items-center gap-3 mt-2 text-sm text-navy-400">
												<span className="flex items-center gap-1.5">
													<LayoutGrid className="w-4 h-4" />
													{canvas.node_count ?? canvas.nodes?.length ?? 0} nodes
												</span>
												<span className="text-navy-300">•</span>
												<span>{formatDate(canvas.updated_at)}</span>
											</div>
										</div>

										{/* Action menu */}
										<div className="shrink-0" onClick={(e) => e.stopPropagation()}>
											<DropdownMenu>
												<DropdownMenuTrigger asChild>
													<button
														className="p-1.5 rounded-lg text-navy-300 hover:text-navy-600 hover:bg-navy-100 transition-colors sm:opacity-0 sm:group-hover:opacity-100"
														title="More actions"
													>
														<MoreVertical className="w-5 h-5" />
													</button>
												</DropdownMenuTrigger>
												<DropdownMenuContent align="end">
													<DropdownMenuItem
														onClick={() => handleDuplicate(canvas.id)}
														disabled={duplicatingId === canvas.id}
													>
														<Copy className="w-4 h-4" />
														<span className="ml-2">
															{duplicatingId === canvas.id ? 'Duplicating...' : 'Duplicate'}
														</span>
													</DropdownMenuItem>
												</DropdownMenuContent>
											</DropdownMenu>
										</div>
									</div>
								</div>
							))}
						</div>
					)}
				</div>

				{/* Quick Links */}
				<div>
					<h2 className="text-lg font-semibold text-navy-700 mb-4">Quick Links</h2>
					<div className="bg-white/80 backdrop-blur-sm rounded-xl p-6 border border-white/80 shadow-sm">
						<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
							<button
								onClick={() => navigate('/organization')}
								className="flex items-center gap-4 p-3 rounded-lg hover:bg-secondary-50 transition-colors text-left group"
							>
								<div className="w-10 h-10 bg-secondary-100 rounded-lg flex items-center justify-center shrink-0">
									<Building2 className="w-5 h-5 text-secondary-600" />
								</div>
								<div>
									<div className="font-medium text-navy-800 group-hover:text-secondary-700 transition-colors">Organization</div>
									<div className="text-sm text-navy-400">Team, brand, and settings</div>
								</div>
							</button>

							<button
								onClick={() => navigate('/organization/gallery')}
								className="flex items-center gap-4 p-3 rounded-lg hover:bg-primary-50 transition-colors text-left group"
							>
								<div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center shrink-0">
									<Image className="w-5 h-5 text-primary-600" />
								</div>
								<div>
									<div className="font-medium text-navy-800 group-hover:text-primary-700 transition-colors">Gallery</div>
									<div className="text-sm text-navy-400">Manage generated images</div>
								</div>
							</button>

							<button
								onClick={() => navigate('/organization/members')}
								className="flex items-center gap-4 p-3 rounded-lg hover:bg-accent-50 transition-colors text-left group"
							>
								<div className="w-10 h-10 bg-accent-100 rounded-lg flex items-center justify-center shrink-0">
									<Users className="w-5 h-5 text-accent-600" />
								</div>
								<div>
									<div className="font-medium text-navy-800 group-hover:text-accent-700 transition-colors">Users</div>
									<div className="text-sm text-navy-400">Members and invitations</div>
								</div>
							</button>

							<button
								onClick={() => navigate('/templates')}
								className="flex items-center gap-4 p-3 rounded-lg hover:bg-indigo-50 transition-colors text-left group"
							>
								<div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center shrink-0">
									<LayoutGrid className="w-5 h-5 text-indigo-600" />
								</div>
								<div>
									<div className="font-medium text-navy-800 group-hover:text-indigo-700 transition-colors">Templates</div>
									<div className="text-sm text-navy-400">Browse and generate</div>
								</div>
							</button>
						</div>
					</div>
				</div>
			</Container>
		</div>
	);
}
